export type PlanTier = 'free' | 'create' | 'collaborate' | 'enterprise';

export interface PlanFeature {
  text: string;
  icon: string; // Icon asset name
  highlight?: boolean;
}

// Display feature from API (admin configuration)
export interface PlanDisplayFeature {
  name: string;
  icon: string;
  featured?: boolean;
}

export interface PlanFeaturesConfig {
  [key: string]: PlanFeature[];
}

/**
 * Plan-based feature mapping for upgrade prompts
 * Based on design specs for each user tier
 */
export const PLAN_UPGRADE_FEATURES: PlanFeaturesConfig = {
  // FREE USER → push to any paid plan
  free: [
    { text: 'Unlimited recordings', icon: 'unlimited-recordings' },
    { text: 'Integrations', icon: 'integrations', highlight: true },
    { text: 'Up to 4k quality', icon: 'quality-4k' },
    { text: 'Admin tools', icon: 'admin-tools' },
    { text: 'AI Smart Actions', icon: 'ai-smart-actions' },
    { text: 'Request links', icon: 'request-links' },
  ],

  // CREATE USER → push to collaborate/team plan(s)
  create: [
    { text: 'Unlimited Smart Actions', icon: 'unlimited-smart-actions' },
    { text: 'More Integrations', icon: 'more-integrations', highlight: true },
    { text: 'Team collaboration', icon: 'team-collaboration' },
    { text: 'Admin tools', icon: 'admin-tools' },
    { text: 'Collections', icon: 'collections' },
    { text: 'More Request links', icon: 'more-request-links' },
  ],

  // COLLABORATE ADMIN → push to enterprise/scale plan(s)
  collaborate: [
    { text: 'SSO/SCIM/SAML', icon: 'sso-scim-saml' },
    { text: 'Premium Integrations', icon: 'premium-integrations', highlight: true },
    { text: 'Domain control', icon: 'domain-control' },
    { text: 'Admin tools', icon: 'admin-tools' },
    { text: 'Greater Compliance', icon: 'greater-compliance' },
    { text: 'More Request links', icon: 'more-request-links' },
  ],

  // Enterprise - fallback (shouldn't be used)
  enterprise: [{ text: 'All features unlocked', icon: 'unlock' }],
};

/**
 * Get upgrade features for a specific plan tier
 * @param planTier - The plan tier to get features for (free, create, collaborate, enterprise)
 * @returns Array of features for that plan tier
 */
export const getFeaturesForPlan = (planTier: PlanTier): PlanFeature[] => {
  return PLAN_UPGRADE_FEATURES[planTier] || PLAN_UPGRADE_FEATURES.free;
};

export const PLAN_TIER_DISPLAY_NAMES: Record<PlanTier, string> = {
  free: 'Share',
  create: 'Create',
  collaborate: 'Collaborate',
  enterprise: 'Scale',
};

export const planTierDisplayName = (planTier: PlanTier): string => PLAN_TIER_DISPLAY_NAMES[planTier] || planTier.charAt(0).toUpperCase() + planTier.slice(1);

/**
 * Map backend Plan#tier string to frontend PlanTier.
 * Backend tier values: 'free', 'share', 'pro', 'create', 'team', 'collaborate', 'business', 'scale'
 */
const TIER_MAP: Record<string, PlanTier> = {
  free: 'free',
  share: 'free',
  pro: 'create',
  create: 'create',
  team: 'collaborate',
  collaborate: 'collaborate',
  business: 'enterprise',
  scale: 'enterprise',
};

export const mapTierToPlanTier = (tier?: string | null): PlanTier => (tier && TIER_MAP[tier]) || 'free';

/**
 * Map organization planCode to plan tier
 * @param planCode - The planCode from organization (e.g., 'pro-monthly', 'team-annual')
 * @returns The corresponding plan tier
 */
export const mapPlanCodeToTier = (planCode?: string | null): PlanTier => {
  // Default to free plan
  if (!planCode) {
    return 'free';
  }

  const planLower = planCode.toLowerCase();

  // Check for collaborate/team/business plans
  if (planLower.includes('team') || planLower.includes('collaborate') || planLower.includes('business')) {
    return 'collaborate';
  }

  // Check for create/pro plans
  if (planLower.includes('pro') || planLower.includes('create')) {
    return 'create';
  }

  // Check for enterprise/scale plans
  if (planLower.includes('enterprise') || planLower.includes('scale')) {
    return 'enterprise';
  }

  // Default to free plan
  return 'free';
};

/**
 * Convert API display features to PlanFeature format
 * @param apiFeatures - Features from API (admin configuration)
 * @returns Array of PlanFeature for display
 */
export const convertApiFeaturesToPlanFeatures = (apiFeatures?: Record<string, PlanDisplayFeature>): PlanFeature[] => {
  if (!apiFeatures || Object.keys(apiFeatures).length === 0) {
    return [];
  }

  return Object.values(apiFeatures).map(feature => ({
    text: feature.name,
    icon: feature.icon,
    highlight: feature.featured,
  }));
};
